'use client';

import { Carousal } from 'components/m1/Carousel';
import {
  OffersCarousel,
  OffersDataType,
} from 'components/ring-air/OffersCarousel';
import React, { useContext } from 'react';
import {
  ProductPurchaseOption,
  ProductPurchaseType,
} from 'content/products';
import { M1ProductData } from 'content/products/m1/IN';
import BirthdayIcon from '../../assets/birthday-icon.svg';
import { M1SelectionContext } from './M1SelectionProvider';
import { Product } from './Product';
// import { CheckoutBtn } from 'components/shared/CheckoutButton';
import Image from 'next/image';
import { CustomImage } from 'components/shared';
import CustomLink from 'components/shared/CustomLink';
import { useSearchParams } from 'next/navigation';
import {
  carousalSection_m1,
  carousalSection_m1_eu_gb,
  carousalSection_m1_in,
  carousalSection_m1_us,
} from 'content/m1';
import { referralCodes } from 'content/referral';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { useCartStore, useShallow } from 'components/shared/Cart';
// import { RegionLocaleContext } from 'pages/_app';
import { Features } from './Features';
import { OutOfStockDisclaimer } from './OutOfStockDisclaimer';
// import { PlantTreesBanner } from 'components/shared/PlantTreesBanner';
import styles from './BuySection.module.css';

const H2 = ({ children }: { children?: React.ReactNode }) => (
  <h2 className={styles.buyH2}>{children}</h2>
);

const OfferFlexContainer = ({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) => (
  <div
    className={`${styles.offerFlexContainer}${className ? ` ${className}` : ''}`}
  >
    {children}
  </div>
);

// Mobile-only product carousel slot. Rendered at all breakpoints so the SSR
// markup is correct without a JS width gate, and hidden on desktop via CSS
// (where the LeftPanel carousel takes over). Replaces the old
// `width < PHONE_MAX && ...` gate that left this absent on first paint and
// caused the hero to pop in after hydration.
const MobileCarouselSection = ({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) => (
  <div
    className={`${styles.mobileCarouselSection}${
      className ? ` ${className}` : ''
    }`}
  >
    {children}
  </div>
);

const OfferTag = ({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) => (
  <p className={`${styles.offerTagBase}${className ? ` ${className}` : ''}`}>
    {children}
  </p>
);

const ProductTitles = {
  [ProductPurchaseOption.oneTimePurchase]: 'One Time Purchase',
  [ProductPurchaseOption.membership]: 'Subscription',
  [ProductPurchaseOption.x]: 'Special Prices',
};

export const BuySection = ({
  className,
  offersData,
}: {
  className?: string;
  offersData?: OffersDataType[];
}) => {
  const searchParams = useSearchParams();

  const { affiliateDetails, campaignActive, store } = useCartStore(
    useShallow((s) => ({
      affiliateDetails: s.affiliateDetails,
      campaignActive: s.campaignActive,
      store: s.store,
    })),
  );
  // const { region } = useRegionLocale();

  const {
    currency,
    productOption,
    changeProductOption,
    product,
    products,
    changeProduct,
    // checkout,
    outOfStock,
    effectiveDiscount,
    m1CampaignActive,
    campaignTitle,
  } = useContext(M1SelectionContext);

  const effectiveStoreForAffiliate = getEffectiveStoreForDiscount(store);

  const affiliateSection =
    !campaignActive &&
    affiliateDetails &&
    affiliateDetails.m1Active &&
    affiliateDetails.discount_configs[
      effectiveStoreForAffiliate as ShopifyStore
    ]?.M1?.code &&
    productOption === ProductPurchaseOption.oneTimePurchase ? (
      <OfferFlexContainer className="offerFlex">
        <OfferTag className={styles.offerTag}>
          {affiliateDetails.image ? (
            <img src={affiliateDetails.image} alt="avatar" />
          ) : null}
          <span>
            <span className={styles.offerApplied}>
              {affiliateDetails.name}{' '}
              {
                affiliateDetails.discount_configs[
                  effectiveStoreForAffiliate as ShopifyStore
                ]?.M1?.code
              }
            </span>{' '}
            Offer Applied
          </span>
        </OfferTag>
      </OfferFlexContainer>
    ) : null;

  const referralSection =
    !campaignActive &&
    typeof window !== 'undefined' &&
    searchParams?.get('referral') &&
    referralCodes[store as ShopifyStore] &&
    productOption === ProductPurchaseOption.oneTimePurchase ? (
      <OfferFlexContainer className="offerFlex">
        <OfferTag className={styles.offerTag}>
          <span>
            <span className={styles.offerApplied}>
              {referralCodes[store as ShopifyStore]?.discountCode}
            </span>{' '}
            Offer Applied
          </span>
        </OfferTag>
      </OfferFlexContainer>
    ) : null;

  const birthdaySection =
    !campaignActive &&
    effectiveDiscount.type === 'birthday' &&
    typeof window !== 'undefined' &&
    searchParams?.get('affiliateCode') === 'birthday' ? (
      <OfferFlexContainer className="offerFlex">
        <OfferTag className={styles.offerTag}>
          <Image
            src={BirthdayIcon}
            alt="birthday_icon"
            style={{
              borderRadius: '0',
            }}
          />
          <span>
            {/* <span className="offerApplied">Your birthday</span>{' '} */}
            Birthday Discount Applied
          </span>
        </OfferTag>
      </OfferFlexContainer>
    ) : null;

  const campaignSection =
    m1CampaignActive &&
    effectiveDiscount.type === 'campaign' &&
    productOption === ProductPurchaseOption.oneTimePurchase ? (
      <p className={`${styles.offerTag} ${styles.transparent}`}>
        <span>{campaignTitle || 'Campaign'} Offer Applied</span>
      </p>
    ) : null;

  console.log({ m1CampaignActive, campaignTitle, productOption });

  // const exclusiveSection =
  //   region === 'US' ? (
  //     <p className="offerTag transparent">
  //       <span>Exclusive Offer Applied</span>
  //     </p>
  //   ) : null;

  const productTypeKeys = Object.keys(products);

  return (
    <div className={`${styles.root}${className ? ` ${className}` : ''}`}>
      <div className={`buy-section ${styles['title-info-content']}`}>
        <div className="title-container">
          <H2>
            {store === ShopifyStore.US ? 'M2 Live' : 'Buy M1 Live'}
            {store === ShopifyStore.US && (
              <span className={styles['lingo-logo']}>
                <CustomImage
                  src="web_v2/m1/Lingo_DFYBA_Logo-Lockup_RGB_Horizontal.png"
                  alt="Lingo - Designed for you by Abbott"
                  width={120}
                  height={30}
                />
              </span>
            )}
          </H2>
          {outOfStock ? <OutOfStockDisclaimer /> : null}
        </div>
        {store === ShopifyStore.US ? (
          <div className={styles['intro-pricing-banner']}>
            At Introductory Pricing
          </div>
        ) : null}
        {
          campaignSection ??
            referralSection ??
            affiliateSection ??
            birthdaySection
          // ?? effectiveDiscount
        }
        <div className={styles['description-container']}>
          {store === ShopifyStore.US ? (
            <p>
              Discover a personalized way to eat better, exercise smarter, and
              make your favourite foods work for you by tracking your glucose
              biomarkers. No prescription needed.
            </p>
          ) : store === ShopifyStore.IN ? (
            <p>
              With a new sensor built for seamless performance, the M1 Live is
              quick, painless, and connects effortlessly via Bluetooth. No
              manual scans — just smooth, continuous glucose insights.
            </p>
          ) : m1CampaignActive ? (
            <p>
              Discover a personalized way to eat better, exercise smarter, and
              make your favourite foods work for you by tracking your
              real-time glucose biomarkers.
              <br />
              <br />
              <span
                style={{
                  color: 'rgba(0,0,0,0.55)',
                }}
              >
                {' '}
                An UltrahumanX member? Head to the mobile app{' '}
                <CustomLink
                  href="https://ultrahuman.onelink.me/QqSM/f7axz8oo"
                  target="_blank"
                  style={{
                    textDecoration: 'underline',
                    color: 'rgba(0,0,0,0.55)',
                  }}
                >
                  app
                </CustomLink>{' '}
                for a personalized experience.
              </span>
            </p>
          ) : (
            <p>
              Discover a personalized way to eat better, exercise smarter, and
              make your favourite foods work for you by tracking your
              real-time glucose biomarkers.
            </p>
          )}
        </div>
      </div>
      <div>
        {/* <Divider fill="rgba(0,0,0,0.07)" /> */}

        {offersData && (
          <OffersCarousel
            data={offersData}
            leftAligned={true}
            className="offer-carousel"
          />
        )}

        <MobileCarouselSection className="carousel-section">
          <Carousal
            items={
              store === ShopifyStore.US
                ? carousalSection_m1_us
                : store === ShopifyStore.IN
                ? carousalSection_m1_in
                : store === ShopifyStore.EU || store === ShopifyStore.GB
                ? carousalSection_m1_eu_gb
                : carousalSection_m1
            }
          />
        </MobileCarouselSection>
        <section className={styles['products-section']}>
          {productTypeKeys.length > 1 ? (
            <div className={styles['product-types']}>
              {productTypeKeys.map((key, index) => {
                return (
                  <button
                    key={index}
                    onClick={() =>
                      changeProductOption(key as ProductPurchaseOption)
                    }
                    className={`gtm ${key === productOption ? styles.active : ''}`}
                    data-buttontype="select plan type"
                  >
                    {ProductTitles[key as ProductPurchaseOption]}
                  </button>
                );
              })}
            </div>
          ) : (
            <div></div>
          )}
          <div className={styles.products}>
            {Object.entries(products[productOption]?.plans ?? {}).map(
              ([key, val]) => (
                <Product
                  key={key}
                  product={val as M1ProductData}
                  currency={currency}
                  selected={key === product}
                  onClick={() => changeProduct(key as ProductPurchaseType)}
                />
              ),
            )}
          </div>
          <Features />
        </section>
        {/* <PlantTreesBanner className="bg-grey" /> */}
      </div>
    </div>
  );
};
